import * as React from 'react'
import { inject, observer } from 'mobx-react'
import Select from 'react-select'
import { debounce } from 'lodash'
import { ProductSearchFilter, INestedProduct, API } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'

interface IProps {
  currentCompanyStore?: CurrentCompanyStore

  onSelect: (product: INestedProduct, label: string) => void

  onEmptySelect?: () => void
  value?: string
  placeholder?: string
  isInventoryProducts?: boolean
  isSellableProducts?: boolean
  clearable?: boolean
  optionFilterFunction?: (products: INestedProduct[]) => INestedProduct[]
  doesNotAllowBatchTrackedProducts?: boolean
}

interface IState {
  isLoading: boolean
  products: INestedProduct[]
}

const debounceTime = 600

@inject('currentCompanyStore')
@observer
export class SellableProductAsyncInput extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      isLoading: false,
      products: [],
    }
  }

  componentDidMount() {
    const { value } = this.props
    this.loadProducts(value)
  }

  async loadProducts(inputValue = '') {
    const company = this.props.currentCompanyStore!.company!
    const filterJson = {
      searchQuery: inputValue,
      selectedSupplierId: undefined,
      selectedCategoryIds: [],
      offset: 0,
      limit: 70,
    }
    const searchFilter = new ProductSearchFilter(filterJson)

    this.setState({ isLoading: true })

    const products = await API.getByProductSearch(company.id, searchFilter)
    this.setState({ isLoading: false, products: products })
  }

  onInputChange = debounce((inputValue) => {
    if (inputValue.length > 0) {
      this.loadProducts(inputValue)
    }
  }, debounceTime)

  getPreFilteredProducts = () => {
    const { products } = this.state
    const { isInventoryProducts } = this.props

    if (isInventoryProducts) {
      return products.filter((p) => p.trackInventory && p.isActive)
    } else {
      return products
    }
  }

  handleOptionFilterLogic = (products: INestedProduct[]) => {
    const { optionFilterFunction } = this.props
    if (optionFilterFunction) {
      return optionFilterFunction(products)
    } else {
      return products
    }
  }

  handleBatchTrackedFilter = (products: INestedProduct[]) => {
    const { doesNotAllowBatchTrackedProducts } = this.props
    if (doesNotAllowBatchTrackedProducts) {
      return products.filter((product) => !product.isBatchTracked)
    } else {
      return products
    }
  }

  render() {
    const {
      onSelect,
      onEmptySelect = () => undefined,
      value,
      clearable = false,
      placeholder = 'Search a product',
    } = this.props
    const { isLoading } = this.state

    const preFilterProducts = this.getPreFilteredProducts()
    const filteredProducts = this.handleOptionFilterLogic(preFilterProducts)
    const products = this.handleBatchTrackedFilter(filteredProducts)

    const options = products.map((p) => ({
      value: p.id,
      label: p.parentProductName ? `${p.parentProductName} - ${p.name}` : p.name,
    }))

    return (
      <Select
        defaultValue={value}
        value={value}
        isLoading={isLoading}
        clearable={clearable}
        placeholder={placeholder}
        onInputChange={this.onInputChange}
        options={options}
        onChange={(option: { label: string; value: string }) => {
          if (option) {
            onSelect(products.find((p) => p.id === option.value)!, option.label)
          } else {
            onEmptySelect()
          }
        }}
        onSelectResetsInput={value ? false : true}
        onBlurResetsInput={value ? false : true}
        filterOptions={() => {
          // Do no filtering, just return all options
          // Allows for searching products by SKU
          // https://github.com/JedWatson/react-select/tree/v1.x
          return options
        }}
      />
    )
  }
}
